import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Pill } from "lucide-react";
import { API, formatINR, daysUntil } from "../lib/api";

function highlight(text, q) {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="sr">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

export default function GlobalSearch({ mode = "navigate", onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const timer = useRef(null);
  const boxRef = useRef(null);
  const nav = useNavigate();

  const doSearch = useCallback(async (query) => {
    if (query.length < 2) { setResults([]); return; }
    try {
      const { data } = await API.get("/medicines/search", { params: { q: query, limit: 8 } });
      setResults(data || []);
      setActive(0);
    } catch {
      setResults([]);
    }
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => doSearch(q), 350);
    return () => clearTimeout(timer.current);
  }, [q, doSearch]);

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        boxRef.current?.querySelector("input")?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const choose = (med) => {
    setOpen(false);
    setQ("");
    if (mode === "pick" && onPick) onPick(med);
    else nav(`/medicine/${med.id}`);
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(results[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative w-full max-w-xl" data-testid="global-search">
      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-[#002FA7]/20 focus-within:border-[#002FA7] transition-all">
        <Search className="w-4 h-4 text-slate-400" strokeWidth={2} />
        <input
          data-testid="global-search-input"
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Search medicines, companies, barcode…"
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400"
        />
        <span className="kbd hidden md:inline-flex">⌘K</span>
      </div>

      {open && q.length >= 2 && (
        <div className="absolute left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg shadow-slate-200/50 overflow-hidden z-50 max-h-[70vh] overflow-y-auto">
          {results.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center">
              No medicines match "{q}"
            </div>
          ) : (
            <ul>
              {results.map((m, idx) => {
                const d = daysUntil(m.expiry_date);
                const expiring = d !== null && d <= 60;
                const outOfStock = (m.quantity || 0) === 0;
                return (
                  <li
                    key={m.id}
                    data-testid={`search-result-${m.id}`}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => choose(m)}
                    className={`px-4 py-3 border-b border-slate-100 cursor-pointer flex items-start gap-3 ${idx === active ? "bg-slate-50" : ""}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#002FA7]/5 text-[#002FA7] flex items-center justify-center shrink-0">
                      <Pill className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{highlight(m.name, q)}</div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        {highlight(m.company, q)} · Batch {m.batch_number} · Loc {m.location || "—"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold text-slate-900">{formatINR(m.mrp)}</div>
                      <div className="mt-1 flex gap-1 justify-end">
                        {outOfStock && <span className="tag tag-danger">Out</span>}
                        {!outOfStock && (m.quantity <= (m.low_stock_threshold || 10)) && <span className="tag tag-warning">Low: {m.quantity}</span>}
                        {!outOfStock && m.quantity > (m.low_stock_threshold || 10) && <span className="tag tag-muted">Qty {m.quantity}</span>}
                        {expiring && <span className="tag tag-danger">Exp {d}d</span>}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
